import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Cookies from 'universal-cookie';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { tokens } from '../../global/theme/tokens';
import usePersistedState from '../../global/usePersistedState';
import { hasPermissions } from '../../auth/hasPermissions';
import { useAiCurrentPageContext, useAiPanel } from './AiChatProvider';
import { useAiChatSession } from './useAiChatSession';
import { useAiAgentPermissions } from './useAiAgentPermissions';
import {
  isAcknowledgementError,
  useAiChatAcknowledgement,
} from './useAiChatAcknowledgement';
import {
  contextProjectId,
  parseReferenceAttachment,
  referenceKey,
} from './aiChatEntities';
import { referenceLabel } from './AiReferenceChip';
import { AiChatHeader } from './AiChatHeader';
import { AiChatMessageList } from './AiChatMessageList';
import { AiChatComposer } from './AiChatComposer';
import { AiChatAcknowledgementGate, AiChatNoticeLine } from './AiChatNotice';

export const AI_PANEL_WIDTH = 420;
const LOW_BUDGET_RATIO = 0.1;
const FULLSCREEN_CONTENT = 'max-w-3xl mx-auto w-full';

export const AiChatPanel = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const {
    enabled,
    compact: isMobile,
    open,
    fullscreen,
    setOpen,
    toggleOpen,
    setFullscreen,
  } = useAiPanel();
  const pageContext = useAiCurrentPageContext();
  const currentUserId = new Cookies().get('userId');

  // nothing is loaded until the panel is opened the first time
  const [hasOpened, setHasOpened] = useState(open);
  const [unseen, setUnseen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const composerRef = useRef(null);
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    if (!open) return;
    setHasOpened(true);
    setUnseen(false);
  }, [open]);

  // how much the model thinks per turn; null = its default (see AiChatOptionsMenu)
  const [reasoningEffort, setReasoningEffort] = usePersistedState(
    'aiChat.reasoningEffort',
    null,
  );

  const projectId = contextProjectId(pageContext);
  const session = useAiChatSession({
    active: enabled && hasOpened,
    projectId,
    reasoningEffort,
    onTurnFinished: () => {
      if (!openRef.current) setUnseen(true);
    },
  });

  // what the AI may do on the user's behalf; nothing until the user opts in
  const permissions = useAiAgentPermissions({ active: enabled && hasOpened });

  // the AI notice every user confirms once before writing (AI Act Art. 50)
  const acknowledgement = useAiChatAcknowledgement({
    active: enabled && hasOpened,
  });

  useEffect(() => {
    if (!open || !fullscreen) return;
    const onKeyDown = (e) => e.key === 'Escape' && setFullscreen(false);
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, fullscreen, setFullscreen]);

  // references the chat already knows are replayed by the backend every turn
  const sentReferenceKeys = useMemo(() => {
    const keys = new Set();
    session.messages
      .filter((m) => m.role === 'user')
      .forEach((m) =>
        (m.attachments || []).forEach((attachment) => {
          const ref = parseReferenceAttachment(attachment);
          if (ref) keys.add(referenceKey(ref));
        }),
      );
    (session.pendingMessage?.localReferences || []).forEach((ref) =>
      keys.add(referenceKey(ref)),
    );
    return keys;
  }, [session.messages, session.pendingMessage]);

  if (!enabled) return null;

  const { chat, budget } = session;
  const isMine = (id) => String(id) === String(currentUserId);
  const mayWriteChats = hasPermissions('ai-chat', 'write');
  const canWrite =
    mayWriteChats &&
    (!chat ||
      isMine(chat.createdBy) ||
      Boolean(
        chat.participants?.some((p) => isMine(p.user?.id) && p.canWrite),
      ));

  // readers never need the notice; writers see it until it is acknowledged
  const needsAcknowledgement =
    mayWriteChats && acknowledgement.acknowledged === false;

  // a 403 "acknowledgement required" means the notice changed: show it again
  const guarded =
    (call) =>
    (...args) =>
      call(...args).catch((err) => {
        if (isAcknowledgementError(err)) acknowledgement.markRequired();
        throw err;
      });
  const send = guarded(session.send);
  const retry = guarded(session.retry);
  const decideApprovals = guarded(session.decideApprovals);

  const scopeLabel = projectId
    ? pageContext.type === 'project'
      ? referenceLabel(pageContext, t)
      : pageContext.projectLabel ||
        referenceLabel({ type: 'project', id: projectId }, t)
    : null;

  // budget: undefined while unknown, null when the instance has no AI
  // budget, else { available, usedPercent, period, tier, periodEndsAt } -
  // relative figures only, the backend discloses no costs or token counts
  const budgetExhausted = Boolean(budget?.available) && budget.usedPercent >= 100;
  const budgetBlocked =
    budget === null || (budget && !budget.available) || budgetExhausted;
  const budgetLow =
    Boolean(budget?.available) &&
    budget.usedPercent >= (1 - LOW_BUDGET_RATIO) * 100;
  const budgetResetsAt = budget?.periodEndsAt
    ? moment(budget.periodEndsAt)
    : null;
  const budgetText = budgetExhausted
    ? budgetResetsAt?.isValid()
      ? t('The AI budget is used up. It resets {{when}}.', {
          when: budgetResetsAt.fromNow(),
        })
      : t('The AI budget is used up.')
    : budgetBlocked
      ? t('AI is currently unavailable because no AI budget is available.')
      : t('The AI budget is almost used up.');

  // the backend retries the failed turn from where it died (lost prompt,
  // interrupted answer or unanswered questions); nothing is sent again here
  const retryLastMessage = () => retry().catch(() => {});

  // the whole panel is a drop zone for uploads, not only the input
  const isFileDrag = (e) =>
    Array.from(e.dataTransfer?.types || []).includes('Files');
  const dropZoneProps =
    canWrite && !needsAcknowledgement
      ? {
        onDragOver: (e) => {
          if (!isFileDrag(e)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
          if (!dragActive) setDragActive(true);
        },
        onDragLeave: (e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setDragActive(false);
        },
        onDrop: (e) => {
          setDragActive(false);
          if (!e.dataTransfer?.files?.length) return;
          e.preventDefault();
          composerRef.current?.addUploads(e.dataTransfer.files);
        },
      }
    : {
        // read-only: refuse the drop instead of letting the browser open the file
        onDragOver: (e) => {
          if (!isFileDrag(e)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'none';
        },
      };

  const content = (
    <Box className="relative flex-1 min-h-0 flex flex-col" {...dropZoneProps}>
      <AiChatHeader
        chats={session.chats}
        chatsLoading={session.chatsLoading}
        chat={chat}
        currentUserId={currentUserId}
        scopeLabel={scopeLabel}
        onSelectChat={session.selectChat}
        onNewChat={session.newChat}
        onDeleteChat={session.deleteChat}
        budget={budget}
        permissions={permissions}
        canEditPermissions={hasPermissions('ai-chat', 'write')}
        reasoningEffort={reasoningEffort}
        onReasoningEffortChange={setReasoningEffort}
        canChangeEffort={hasPermissions('ai-chat', 'write')}
        fullscreen={fullscreen}
        onToggleFullscreen={() => setFullscreen(!fullscreen)}
        onClose={() => setOpen(false)}
        isMobile={isMobile}
      />
      {(budgetBlocked || budgetLow) && (
        <Box className={`px-4 ${fullscreen ? FULLSCREEN_CONTENT : ''}`}>
          <Box
            className="flex items-center gap-2 px-3 py-2 rounded-lg"
            sx={{
              backgroundColor: `${budgetBlocked ? colors.error : colors.warning}15`,
              border: `1px solid ${budgetBlocked ? colors.error : colors.warning}40`,
            }}
          >
            <WarningAmberIcon
              sx={{
                fontSize: 18,
                color: budgetBlocked ? colors.error : colors.warning,
              }}
            />
            <Typography variant="caption">{budgetText}</Typography>
          </Box>
        </Box>
      )}
      {needsAcknowledgement ? (
        <AiChatAcknowledgementGate
          saving={acknowledgement.saving}
          onAcknowledge={() =>
            acknowledgement
              .acknowledge()
              // drop the "acknowledge first" error a refused send left behind
              .then(session.clearError)
              .catch(() => {})
          }
        />
      ) : (
        <>
          <AiChatMessageList
            messages={session.messages}
            pendingMessage={session.pendingMessage}
            streamingMessage={session.streamingMessage}
            status={chat?.status}
            isBusy={session.isBusy}
            isLoading={session.messagesLoading || session.chatsLoading}
            pageContext={pageContext}
            onRetry={canWrite && !budgetBlocked ? retryLastMessage : null}
            lastError={chat?.lastError}
            pendingApprovals={session.pendingApprovals}
            canDecideApprovals={canWrite}
            onDecideApprovals={decideApprovals}
            contentClassName={fullscreen ? FULLSCREEN_CONTENT : undefined}
          />
          <Box className={fullscreen ? FULLSCREEN_CONTENT : undefined}>
            <AiChatComposer
              ref={composerRef}
              pageContext={pageContext}
              scopeProject={
                projectId ? { id: projectId, label: scopeLabel } : null
              }
              sentReferenceKeys={sentReferenceKeys}
              canWrite={canWrite}
              isBusy={session.isBusy}
              // sending before the list is there would race the scope's chat selection
              blocked={budgetBlocked || session.chatsLoading}
              error={session.error}
              onClearError={session.clearError}
              onSend={send}
              focusKey={
                open && !isMobile ? session.activeChatId || 'new' : null
              }
            />
            <AiChatNoticeLine />
          </Box>
        </>
      )}
      {dragActive && !needsAcknowledgement && <DropOverlay />}
    </Box>
  );

  if (isMobile)
    return open ? (
      <Box
        className="fixed inset-0 flex flex-col"
        sx={{ zIndex: 1250, backgroundColor: colors.glassSolid }}
      >
        {content}
      </Box>
    ) : null;

  return (
    <Box
      component="aside"
      className="relative h-full flex-shrink-0"
      sx={{
        width: open ? AI_PANEL_WIDTH : 0,
        flexGrow: open && fullscreen ? 1 : 0,
        transition: 'width 0.3s ease, flex-grow 0.3s ease',
        zIndex: 40,
      }}
    >
      <AiChatHandle
        open={open}
        busy={session.isBusy}
        unseen={unseen}
        onClick={toggleOpen}
      />
      <Box
        className="h-full overflow-hidden backdrop-blur-lg"
        sx={{
          backgroundColor: colors.glass,
          visibility: open ? 'visible' : 'hidden',
          transition: `visibility 0s linear ${open ? '0s' : '0.3s'}`,
        }}
      >
        <Box
          className="h-full flex flex-col"
          sx={{ width: fullscreen ? '100%' : AI_PANEL_WIDTH }}
        >
          {content}
        </Box>
      </Box>
    </Box>
  );
};

const AiChatHandle = ({ open, busy, unseen, onClick }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const highlight = busy || unseen;

  return (
    <Tooltip
      title={open ? t('Close AI chat') : t('Open AI chat')}
      placement="left"
      disableInteractive
    >
      <Box
        component="button"
        type="button"
        onClick={onClick}
        aria-label={open ? t('Close AI chat') : t('Open AI chat')}
        className="absolute flex flex-col items-center justify-center gap-0.5 backdrop-blur-lg"
        sx={{
          left: 0,
          top: '50%',
          transform: 'translate(-100%, -50%)',
          width: 22,
          height: 60,
          border: 'none',
          borderRadius: '12px 0 0 12px',
          backgroundColor: colors.glass,
          color: highlight ? colors.info : colors.text,
          cursor: 'pointer',
          boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.12)',
          transition: 'width 0.2s ease, color 0.2s ease',
          animation: busy ? 'ai-handle-pulse 2s ease-in-out infinite' : 'none',
          '@keyframes ai-handle-pulse': {
            '0%, 100%': { boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.12)' },
            '50%': { boxShadow: `-2px 0 18px ${colors.info}99` },
          },
          '& .ai-handle-label': {
            maxHeight: 0,
            opacity: 0,
            transition: '0.2s ease',
          },
          '&:hover': {
            width: 32,
            color: colors.info,
            '& .ai-handle-label': { maxHeight: 16, opacity: 1 },
          },
        }}
      >
        {open ? (
          <ChevronRightIcon sx={{ fontSize: 20 }} />
        ) : (
          <>
            <AutoAwesomeIcon sx={{ fontSize: 16 }} />
            <Typography
              className="ai-handle-label"
              sx={{ fontSize: '0.6rem', fontWeight: 700, lineHeight: 1 }}
            >
              AI
            </Typography>
          </>
        )}
        {unseen && !open && (
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              right: 5,
              width: 7,
              height: 7,
              borderRadius: '50%',
              backgroundColor: colors.info,
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

const DropOverlay = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  return (
    <Box
      className="absolute inset-2 flex flex-col items-center justify-center gap-2 z-20 pointer-events-none text-center px-6"
      sx={{
        backgroundColor: `${colors.info}15`,
        border: `2px dashed ${colors.info}`,
        borderRadius: '16px',
        backdropFilter: 'blur(2px)',
        animation: 'ai-drop-in 0.15s ease-out',
        '@keyframes ai-drop-in': {
          from: { opacity: 0, transform: 'scale(0.98)' },
          to: { opacity: 1, transform: 'scale(1)' },
        },
      }}
    >
      <CloudUploadIcon sx={{ fontSize: 44, color: colors.info }} />
      <Typography fontWeight={600}>
        {t('Drop files to attach them')}
      </Typography>
      <Typography variant="caption" sx={{ opacity: 0.6 }}>
        {t('Max. 5 MB per file')}
      </Typography>
    </Box>
  );
};
